import { Layer, LayerType, TemplateInfo } from '../types';

/** Rozmiar canvasu podglądu w LayersDialog – punkt odniesienia dla transformacji */
const LAYERS_CANVAS = 260;
/** Domyślna długość dłuższego boku tekstury (podgląd 3D w czasie rzeczywistym) */
const TEX_LONG_DEFAULT = 512;

// ── Helpers ──────────────────────────────────────────────────

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('image load error'));
    img.src = src;
  });
}

/**
 * Rysuje warstwy pasujące do listy typów na podany kontekst.
 * Kolejność: od dołu stosu (ostatni indeks) do góry (indeks 0).
 */
function drawFiltered(
  ctx:       CanvasRenderingContext2D,
  layers:    Layer[],
  imgs:      HTMLImageElement[],
  types:     LayerType[],
  cx:        number,
  cy:        number,
  drawScale: number,
): void {
  for (let i = layers.length - 1; i >= 0; i--) {
    if (!types.includes(layers[i].type)) continue;
    const l = layers[i];
    ctx.save();
    ctx.translate(cx + l.translateX * drawScale, cy + l.translateY * drawScale);
    ctx.rotate((l.rotate * Math.PI) / 180);
    const dw = imgs[i].naturalWidth  * l.baseScale * l.scale * drawScale;
    const dh = imgs[i].naturalHeight * l.baseScale * l.scale * drawScale;
    ctx.drawImage(imgs[i], -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }
}

/**
 * Buduje maskę z obrazu szablonu.
 * Białe piksele szablonu → alpha = 255 (inside), czarne → alpha = 0.
 * invert = true: odwraca (do warstw na zewnątrz otworu).
 */
function buildMask(
  tplImg:  HTMLImageElement,
  texW:    number,
  texH:    number,
  tplX:    number,
  tplY:    number,
  tplW:    number,
  tplH:    number,
  invert:  boolean,
): HTMLCanvasElement {
  const c   = document.createElement('canvas');
  c.width   = texW;
  c.height  = texH;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(tplImg, tplX, tplY, tplW, tplH);
  const id = ctx.getImageData(0, 0, texW, texH);
  for (let i = 0; i < id.data.length; i += 4) {
    const b = (id.data[i] + id.data[i+1] + id.data[i+2]) / 3;
    // Hard binary threshold – eliminates semi-transparent boundary pixels
    // that would otherwise reveal the material-colour background as a thin ring.
    const inside = b >= 128;
    id.data[i+3] = (invert ? !inside : inside) ? 255 : 0;
  }
  ctx.putImageData(id, 0, 0);
  return c;
}

// ── Główna funkcja ────────────────────────────────────────────

/**
 * Generuje canvas z kompozytem warstw na tle koloru materiału,
 * z uwzględnieniem szablonu (masking po typie warstwy):
 *   S (Shaped) + A (All) → widoczne WEWNĄTRZ otworu szablonu
 *   F (Flat)   + A (All) → widoczne NA ZEWNĄTRZ otworu szablonu
 *   Bez szablonu: wszystkie warstwy widoczne wszędzie.
 */
export async function buildCompositeTexture(
  layers:          Layer[],
  materialColor:   string,
  materialWidthMm:  number,
  materialHeightMm: number,
  template:        TemplateInfo | null,
  longSidePx:      number = TEX_LONG_DEFAULT,
): Promise<HTMLCanvasElement> {
  const texW = materialWidthMm >= materialHeightMm
    ? longSidePx
    : Math.round(longSidePx * materialWidthMm  / materialHeightMm);
  const texH = materialHeightMm >= materialWidthMm
    ? longSidePx
    : Math.round(longSidePx * materialHeightMm / materialWidthMm);

  const main = document.createElement('canvas');
  main.width  = texW;
  main.height = texH;
  const mctx  = main.getContext('2d')!;

  mctx.fillStyle = materialColor;
  mctx.fillRect(0, 0, texW, texH);

  if (layers.length === 0) return main;

  const layerImgs = await Promise.all(layers.map(l => loadImg(l.imageDataUrl)));

  const drawScale = longSidePx / LAYERS_CANVAS;
  const cx = texW / 2;
  const cy = texH / 2;

  // ── Bez szablonu: wszystkie warstwy ───────────────────────
  if (!template) {
    drawFiltered(mctx, layers, layerImgs, ['S', 'F', 'A'], cx, cy, drawScale);
    return main;
  }

  // ── Z szablonem ───────────────────────────────────────────

  const tplImg   = await loadImg(template.dataUrl);
  const tplW     = template.widthMm  * texW / materialWidthMm;
  const tplH     = template.heightMm * texH / materialHeightMm;
  const tplX     = (texW - tplW) / 2;
  const tplY     = (texH - tplH) / 2;

  const insideMask  = buildMask(tplImg, texW, texH, tplX, tplY, tplW, tplH, false);
  const outsideMask = buildMask(tplImg, texW, texH, tplX, tplY, tplW, tplH, true);

  // Warstwy S+A → wewnątrz otworu
  const insideC = document.createElement('canvas');
  insideC.width = texW; insideC.height = texH;
  const ictx = insideC.getContext('2d')!;
  drawFiltered(ictx, layers, layerImgs, ['S', 'A'], cx, cy, drawScale);
  ictx.globalCompositeOperation = 'destination-in';
  ictx.drawImage(insideMask, 0, 0);

  // Warstwy F+A → na zewnątrz otworu
  const outsideC = document.createElement('canvas');
  outsideC.width = texW; outsideC.height = texH;
  const octx = outsideC.getContext('2d')!;
  drawFiltered(octx, layers, layerImgs, ['F', 'A'], cx, cy, drawScale);
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(outsideMask, 0, 0);

  // Kompozyt na tle materiału
  mctx.drawImage(outsideC, 0, 0);
  mctx.drawImage(insideC,  0, 0);

  return main;
}
